// The onboarding gate dots, shared by the pipeline board and the member page.
import { cn } from "@/lib/utils";
import { addressSettled, isSuitePath } from "@/lib/onboardingComplete";

export type GateState = "passed" | "partial" | "pending";

export type Gate = {
  key: string;
  label: string;
  state: GateState;
  /** When this gate was passed, if we know. */
  at: string | null;
  /** What we tell the team when this is the gate they are waiting on. */
  waiting: string;
};

export type GateRecord = {
  una_name?: string | null;
  una_name_status?: string | null;
  una_name_confirmed_at?: string | null;
  mea_status?: string | null;
  mea_signed_at?: string | null;
  ca_address_option?: string | null;
  suite_number?: number | null;
  lease_status?: string | null;
  lease_signed_at?: string | null;
  payment_status?: string | null;
  legal_address?: unknown;
  created_at?: string | null;
};

function hasLegalAddress(value: unknown) {
  if (!value || typeof value !== "object") return false;
  const a = value as Record<string, unknown>;
  return Boolean(a.line1 || a.city || a.zip);
}

export function gatesFor(r: GateRecord): Gate[] {
  const intake = hasLegalAddress(r.legal_address) || Boolean(r.una_name);
  const nameSubmitted = r.una_name_status === "submitted" || r.una_name_status === "change_requested";
  const nameApproved = r.una_name_status === "approved";
  const mea = r.mea_status === "signed" || Boolean(r.mea_signed_at);
  const confirmed = Boolean(r.una_name_confirmed_at);
  const addressChosen = Boolean(r.ca_address_option) || r.suite_number != null;
  const leaseSigned = r.lease_status === "signed" || Boolean(r.lease_signed_at);
  return [
    {
      key: "intake",
      label: "Intake",
      state: intake ? "passed" : "pending",
      at: intake ? r.created_at ?? null : null,
      waiting: "Filling in their details",
    },
    {
      key: "name",
      label: "Name",
      state: nameApproved ? "passed" : nameSubmitted ? "partial" : "pending",
      at: null,
      waiting: nameSubmitted ? "Waiting on name review" : "Choosing a name",
    },
    {
      key: "mea",
      label: "MEA",
      state: mea ? "passed" : "pending",
      at: r.mea_signed_at ?? null,
      waiting: "Waiting on their signature",
    },
    {
      key: "confirmed",
      label: "Confirmed",
      state: confirmed ? "passed" : "pending",
      at: r.una_name_confirmed_at ?? null,
      waiting: "Confirming their record",
    },
    {
      key: "address",
      label: "Address",
      state: addressSettled(r as never) ? "passed" : addressChosen ? "partial" : "pending",
      at: null,
      waiting: addressChosen ? "Settling their address" : "Choosing an address",
    },
    // The lease belongs to the suite path only. Members with their own address
    // never sign one, so it is not a gate for them.
    ...(isSuitePath(r as never)
      ? [
          {
            key: "lease",
            label: "Lease",
            state: (leaseSigned ? "passed" : "pending") as GateState,
            at: r.lease_signed_at ?? null,
            waiting: "Waiting on the lease signature",
          },
        ]
      : []),
  ];
}

export function GateStrip({
  record,
  gates,
  showPaid = true,
  className,
}: {
  record: GateRecord;
  gates?: Gate[];
  showPaid?: boolean;
  className?: string;
}) {
  const list = gates ?? gatesFor(record);
  return (
    <div className={cn("flex items-end gap-2", className)}>
      {list.map((g) => (
        <div key={g.key} className="flex w-12 flex-col items-center gap-1">
          <span
            className={cn(
              "h-3 w-3 rounded-full border",
              g.state === "passed"
                ? "border-foreground bg-foreground"
                : g.state === "partial"
                  ? "border-foreground bg-gradient-to-r from-foreground from-50% to-transparent to-50%"
                  : "border-muted-foreground/40 bg-transparent",
            )}
            aria-hidden
          />
          <span className="text-[10px] leading-none text-muted-foreground">{g.label}</span>
        </div>
      ))}
      {showPaid && isSuitePath(record as never) ? (
        <div className="ml-3 flex w-12 flex-col items-center gap-1 border-l pl-3 opacity-70">
          <span
            className={cn(
              "h-2 w-2 rounded-full border",
              record.payment_status === "complete"
                ? "border-muted-foreground bg-muted-foreground"
                : "border-muted-foreground/40 bg-transparent",
            )}
            aria-hidden
          />
          <span className="text-[10px] leading-none text-muted-foreground">Paid</span>
        </div>
      ) : null}
    </div>
  );
}
